// The dashboard sidebar's destinations, in rail order. Split out of Sidebar.tsx
// (BL-2422); the rail and the phone drawer both render this one list.

import { ArrowLeftRight, AtSign, ClipboardList, Gavel, Settings, Users, Package, Shield, FileText, Receipt, Cloud } from 'lucide-react';
import type { ComponentType } from 'react';
import type { Grant } from '@auctogon/core-admin/api/grants';
import { OFFLINE_PAGE_PATH } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';
import { STORAGE_AND_SYNC_TITLE } from '../offline/offline-copy';

/**
 * Which surface this Sidebar is rendering as (BL-1439).
 *
 * `rail` is the pinned column in `DashboardShell` at >=768px — collapsible,
 * cookie-backed, unchanged. `drawer` is the same nav inside the phone
 * hamburger drawer: always expanded (a drawer has no collapsed tier), and it
 * never touches the collapse cookie, so a phone session cannot clobber the
 * operator's desktop preference.
 *
 * This is a variant naming WHICH SURFACE this is, not a domain value — the
 * shape `self-contained-view-units.md` (Cluster 3) permits on a shared unit.
 */
export type SidebarVariant = 'rail' | 'drawer';

/** One sidebar destination. */
export interface NavItem {
  name: string;
  href: string;
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  /**
   * A count the entry carries, named by what it counts (a variant, never the number): the Storage &
   * Sync entry shows, in red, the change queue's rows that need someone (BL-2569), the Inbox its
   * unread mentions (BL-2491).
   */
  count?: 'change-queue-issues' | 'inbox-unread';
  /**
   * The grant the entry's page needs, for an entry shown locked rather than hidden to a role
   * without it (ADR 030 — Design Canon, "Role-gated UI is locked, not hidden"; BL-2519): it renders
   * in place with a padlock, names who can open it, and still opens the page's No access state.
   * Every entry some tenant role can't open carries one (BUG-2187), so no entry hides by role.
   * The one exception is Super Admin, which ADR 030's closed list keeps hidden.
   */
  lockedGrant?: Grant;
}

/** A titled group of destinations. */
export interface NavSection {
  title: string;
  items: NavItem[];
  /** When true, section is only visible inside the native admin app (BL-2342). */
  nativeAppOnly?: boolean;
}

/** Every sidebar section, top to bottom. */
export const navigation: NavSection[] = [
  {
    title: 'Main',
    items: [
      // BL-2467 (D6): the Inbox sits at the top, for every role. The label has
      // no @ because the icon is already an @ (Dan Saper, Oct 14, 2026, BL-2491),
      // and the entry carries the live unread count (BL-2491).
      { name: 'Inbox', href: '/inbox', icon: AtSign, count: 'inbox-unread' },
      { name: 'Auctions', href: '/auctions', icon: Gavel },
      { name: 'Inventory', href: '/inventory', icon: Package, lockedGrant: 'inventory.read' },
    ],
  },
  {
    title: 'CRM',
    items: [
      { name: 'Customers', href: '/customers', icon: Users, lockedGrant: 'customer.read' },
    ],
  },
  {
    // BL-2519 (PRJ-98 SHARE-7): Accounting's own section, between CRM and
    // Management, one entry per record list. Payments joins when its list
    // ships. Each is gated by NAV_PERMISSIONS and shown locked, never hidden.
    title: 'Accounting',
    items: [
      // BL-2896 (PRJ-108 SUMM-1): one auction's money in seven cards, the section's first item.
      // Gated on invoice.read alone: every role holding it also holds settlement.read
      // (pinned by role-grants.auction-summary.test.ts); the route also requires settlement.read.
      { name: 'Auction summary', href: '/auction-summary', icon: ClipboardList, lockedGrant: 'invoice.read' },
      { name: 'Invoices', href: '/invoices', icon: FileText, lockedGrant: 'invoice.read' },
      { name: 'Settlements', href: '/settlements', icon: Receipt, lockedGrant: 'settlement.read' },
      // BL-2824 (PRJ-105 D1): every money movement in one list, the section's third item.
      { name: 'Transactions', href: '/transactions', icon: ArrowLeftRight, lockedGrant: 'payment.read' },
    ],
  },
  {
    title: 'Management',
    items: [
      { name: 'Settings', href: '/settings', icon: Settings, lockedGrant: 'settings.read' },
      // The one entry still hidden by role: ADR 030 — Design Canon's closed list
      // (`canSeeNavItem` shows it to a super admin only).
      { name: 'Super Admin', href: '/super-admin', icon: Shield },
    ],
  },
  {
    // BL-2342: the phone app's Offline section. Its one entry is Storage & Sync
    // (BL-2566): the mode control, the change queue and the device's storage.
    // Desktop never shows it (ADR 066 — Offline Mode § 5). Locked, not hidden, for a role the page
    // refuses (BL-2685 AC 7): inventory.read is the grant behind the page's inventory.view gate.
    title: 'Offline',
    nativeAppOnly: true,
    items: [
      { name: STORAGE_AND_SYNC_TITLE, href: OFFLINE_PAGE_PATH, icon: Cloud, count: 'change-queue-issues', lockedGrant: 'inventory.read' },
    ],
  },
];
